import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { ResearchLineageNodeProjection } from "@roboscientist/contracts";
import { OverflowReveal } from "@roboscientist/ui";
import { ArrowUpRight, PackageOpen, Pause, Play, RotateCcw, Settings2 } from "lucide-react";

import { layoutResearchLineage, lineageRefKey } from "../../web/src/components/researchLineageLayout.js";
import { englishGalleryText } from "./galleryEnglish.js";
import type { GalleryStudy } from "./publicGallery.js";

const countLabel = (count: number, singular: string, plural = `${singular}s`) => `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

function GalleryHorizon() {
  return <section className="gallery-horizon" id="next" aria-labelledby="gallery-horizon-title">
    <header className="gallery-horizon__meta">
      <span>NEXT / IN DEVELOPMENT</span>
      <span className="gallery-horizon__status"><i aria-hidden="true" /> Coming soon</span>
    </header>
    <div className="gallery-horizon__layout">
      <div className="gallery-horizon__intro">
        <h2 id="gallery-horizon-title">More ways to turn<br />experiments into evidence.</h2>
        <p>New research workflows are in development.</p>
      </div>
      <ol className="gallery-horizon__list" aria-label="Upcoming RoboScientist capabilities">
        <li><span>01</span><strong>Run comparison</strong><em>In development</em></li>
        <li><span>02</span><strong>Evidence trace</strong><em>In development</em></li>
        <li><span>03</span><strong>Research publishing</strong><em>In development</em></li>
      </ol>
    </div>
  </section>;
}

function LineagePreview({ study }: { study: GalleryStudy }) {
  const preview = useMemo(() => {
    // The layout reads only the lineage fields retained in the public index.
    const layout = layoutResearchLineage(study.lineageNodes as ResearchLineageNodeProjection[]);
    const minX = Math.min(...layout.nodes.map(node => node.x));
    const maxX = Math.max(...layout.nodes.map(node => node.x));
    const minY = Math.min(...layout.nodes.map(node => node.y));
    const maxY = Math.max(...layout.nodes.map(node => node.y));
    const x = (value: number) => maxX === minX ? 200 : 40 + (value - minX) / (maxX - minX) * 320;
    const y = (value: number) => maxY === minY ? 92 : 38 + (value - minY) / (maxY - minY) * 108;
    const details = new Map(study.lineageNodes.map(node => [lineageRefKey(node.ref), node]));
    const nodes = layout.nodes.map(node => ({ ...node, x: x(node.x), y: y(node.y), outcome: details.get(node.key)?.latestAssessment?.outcome ?? "unassessed" }));
    return { nodes, byKey: new Map(nodes.map(node => [node.key, node])), edges: layout.edges };
  }, [study.lineageNodes]);
  return <svg className="gallery-lineage-preview" viewBox="0 0 400 184" aria-hidden="true">
    {preview.edges.map(edge => {
      const parent = preview.byKey.get(edge.parentKey), child = preview.byKey.get(edge.childKey);
      if (!parent || !child) return null;
      const bend = (child.x - parent.x) / 2;
      return <path key={edge.id} className={edge.secondary ? "is-secondary" : undefined} d={`M${parent.x},${parent.y} C${parent.x + bend},${parent.y} ${child.x - bend},${child.y} ${child.x},${child.y}`} />;
    })}
    {preview.nodes.map(node => <g key={node.key} className={`gallery-preview-node gallery-preview-node--${node.outcome}`}>
      <circle cx={node.x} cy={node.y} r="5" />
    </g>)}
  </svg>;
}

function SnapshotCard({ study, index, onOpen, presentationTime }: {
  study: GalleryStudy;
  index: number;
  onOpen: () => void;
  presentationTime?: number;
}) {
  return <article className="gallery-card"
    style={{ "--card-index": index, "--card-accent": ["var(--blue)", "var(--green)", "var(--violet)"][index % 3] } as CSSProperties}>
    <div className="gallery-card__tilt"><div className="gallery-card__flip" style={presentationTime == null ? undefined : { animation: "none", transform: `rotateY(${-180 * Math.pow(1 - Math.max(0, Math.min(1, (presentationTime - index * .1) / 1.1)), 3)}deg)` }}>
      <div className="gallery-card__back" aria-hidden="true">
        <span className="gallery-card__back-brand">RoboScientist.</span>
        <span className="gallery-card__back-label">RESEARCH ARCHIVE <b>{String(index + 1).padStart(2, "0")}</b></span>
      </div>
      <button className="gallery-card__front" type="button" onClick={onOpen} aria-label={`Open snapshot: ${englishGalleryText(study.snapshot.title)}`}>
        <div className="gallery-card__art" aria-hidden="true">
          <span className="gallery-card__edition">STUDY {String(index + 1).padStart(2, "0")}</span>
          <LineagePreview study={study} />
          <ArrowUpRight className="gallery-card__arrow" size={19} />
        </div>
        <div className="gallery-card__content">
          <header><time dateTime={study.snapshot.exportedAt}>{new Date(study.snapshot.exportedAt).toLocaleDateString("en", { year: "numeric", month: "short", day: "numeric" })}</time><span>{study.hasPaper ? "Paper available" : "Research snapshot"}</span></header>
          <h2>{englishGalleryText(study.snapshot.title)}</h2>
          <OverflowReveal as="p" value={englishGalleryText(study.snapshot.summary)} label="Snapshot summary" />
          <dl>
            <div><dt>Evolution nodes</dt><dd>{study.counts.evolutionNodes}</dd></div>
            <div><dt>{study.counts.experiments === 1 ? "Experiment" : "Experiments"}</dt><dd>{study.counts.experiments}</dd></div>
            <div><dt>{study.counts.runs === 1 ? "Run" : "Runs"}</dt><dd>{study.counts.runs}</dd></div>
            <div><dt>{study.counts.reports === 1 ? "Report" : "Reports"}</dt><dd>{study.counts.reports}</dd></div>
          </dl>
          <footer><span>{study.snapshot.sourceProject.name}</span><strong>Explore study <ArrowUpRight size={15} /></strong></footer>
        </div>
      </button>
    </div></div>
  </article>;
}

function SnapshotGrid({ studies, onOpen, presentationTime }: {
  studies: GalleryStudy[];
  onOpen: (snapshotId: string) => void;
  presentationTime?: number;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (presentationTime != null) return;
    const grid = gridRef.current;
    if (!grid) return;
    // One start time for the whole collection, so row wrapping cannot reorder flips.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setEntered(true);
        observer.disconnect();
      }
    });
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  return <div ref={gridRef} className={`gallery-grid ${presentationTime != null || entered ? "is-revealed" : "is-pending"}`}>
    {studies.map((study, index) => <SnapshotCard key={study.snapshot.id} study={study} index={index} presentationTime={presentationTime} onOpen={() => onOpen(study.snapshot.id)} />)}
  </div>;
}

export function GalleryHome({ studies, loading, onManage, onOpen, presentationTime }: {
  studies: GalleryStudy[];
  loading: boolean;
  onManage?: () => void;
  onOpen: (snapshotId: string) => void;
  presentationTime?: number;
}) {
  const [replay, setReplay] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const motionOff = paused || reducedMotion;
  const totalRuns = studies.reduce((total, study) => total + study.counts.runs, 0);
  const totalNodes = studies.reduce((total, study) => total + study.counts.evolutionNodes, 0);

  return <div className={`gallery-home ${motionOff ? "is-motion-paused" : ""}`}>
    <section key={`hero-${replay}`} className="gallery-hero" aria-labelledby="gallery-title">
      <div className="gallery-hero__copy">
        <span className="gallery-eyebrow">RESEARCH, IN MOTION</span>
        <h1 id="gallery-title">Experiment<br />Gallery<span>.</span></h1>
      </div>
      <div className="gallery-hero__intro">
        <p>From the first idea to the final paper.<br />Explore the experiments, iterations, and evidence behind each discovery.</p>
        <div className="gallery-hero__facts" aria-label="Collection overview">
          <span><strong>{loading ? "—" : String(studies.length).padStart(2, "0")}</strong> {studies.length === 1 ? "study" : "studies"}</span>
          <span><strong>{loading ? "—" : totalNodes.toLocaleString()}</strong> {totalNodes === 1 ? "evolution node" : "evolution nodes"}</span>
          <span><strong>{loading ? "—" : totalRuns.toLocaleString()}</strong> {totalRuns === 1 ? "run" : "runs"}</span>
        </div>
      </div>
    </section>
    <div className="gallery-collection-bar">
      <div><h2>Explore the collection</h2><span className="gallery-collection-count">{loading ? "Loading collection" : countLabel(studies.length, "saved snapshot")}</span></div>
      <div className="gallery-motion-controls" aria-label="Gallery animation controls">
        <button type="button" onClick={() => setPaused((value) => !value)} aria-pressed={motionOff} disabled={reducedMotion} title={reducedMotion ? "Reduced motion is enabled in your system settings" : undefined}>{motionOff ? <Play size={13} /> : <Pause size={13} />}<span>{motionOff ? "Resume motion" : "Pause motion"}</span></button>
        <button type="button" className="gallery-replay" disabled={loading || reducedMotion} onClick={() => { setPaused(false); setReplay((value) => value + 1); }}><RotateCcw size={13} /> Replay intro</button>
      </div>
    </div>
    {loading ? <div className="page-loading"><span /> Loading published studies…</div> : studies.length ? <SnapshotGrid key={`cards-${replay}`} studies={studies} onOpen={onOpen} presentationTime={presentationTime} /> : <section className="gallery-empty-state">
      <span><PackageOpen size={28} /></span><strong>No experiment snapshots yet</strong>
      <p>{onManage ? <>Open Manage to import a <code>.rsexperiment.zip</code> package.</> : "Published studies will appear here."}</p>
      {onManage ? <button className="button button--primary" type="button" onClick={onManage}><Settings2 size={15} /> Manage snapshots</button> : null}
    </section>}
    <GalleryHorizon />
  </div>;
}
